import {
  Box, Grid, Typography, Paper, TextField, InputAdornment, Autocomplete, Chip, useTheme, styled, Button, Tooltip, Avatar,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import { useNavigate } from "react-router-dom";
import {
  PriorityHigh as PriorityHighIcon, // New icon for Unassigned
  DoneAll as DoneAllIcon,
  Cancel as CancelIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Autorenew as AutorenewIcon,
  Pause as PauseIcon,
  KeyboardBackspace as KeyboardBackspaceIcon,
  ListAlt as ListAltIcon, // Icon for Total Tickets
  Equalizer as EqualizerIcon
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { selectGlobalProperty } from "../../../../redux/propertySlice";
import axiosInstance from "../../../../lib/axios";
import TaskAcceptDialog from "./TaskAcceptDialog";
import TaskAcceptanceTable from "./TaskAcceptanceTable";

const StyledCard = styled(Paper)(({ theme }) => ({
  borderRadius: 20,
  transition: 'transform 0.4s ease, box-shadow 0.4s ease',
  '&:hover': {
    transform: 'translateY(-6px)',
    boxShadow: theme.shadows[10],
  },
  background: `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[100]} 100%)`,
  border: `1px solid ${theme.palette.divider}`,
  overflow: 'hidden',
  padding: theme.spacing(0.5),
  paddingLeft: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1),
    paddingLeft: theme.spacing(2),
    minHeight: '70px',
  },
}));

const StyledAvatar = styled('div')(({ theme, status }) => ({
  backgroundColor:
    status === 'O' ? theme.palette.error.main : // Unassigned
      status === 'I' ? theme.palette.warning.main : // In Progress
        status === 'R' ? '#FF5722' : // Resolved
          status === 'C' ? theme.palette.success.main : // Closed
            status === '' ? '#8e24aa' : // Total Tickets
              theme.palette.primary.light, // Default
  width: 32,
  height: 32,
  boxShadow: `0 2px 8px ${theme.palette.grey[400]}`,
  border: `2px solid ${theme.palette.background.paper}`,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '1.2rem', // Ensure icon size is consistent
  [theme.breakpoints.down('sm')]: {
    width: 28,
    height: 28,
    fontSize: '1rem', // Adjust icon size for smaller screens
  },
}));

const getStatusText = (status) => {
  switch (status) {
    case 'O': return 'Open';
    case 'I': return 'In Progress';
    case 'R': return 'Resolved';
    case 'C': return 'Closed';
    default: return 'Unknown';
  }
};

export default function TaskAcceptance() {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [categories, setCategories] = useState([]);
  const [warden, setWarden] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedWarden, setSelectedWarden] = useState(null);
  const [rows, setRows] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [isInitialFetch, setIsInitialFetch] = useState(true);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [multiDialogOpen, setMultiDialogOpen] = useState(false);
  const [selectedRowCategoryId, setSelectedRowCategoryId] = useState(null);
  const [ticketCounts, setTicketCounts] = useState({
    open: 0,
    inprogress: 0,
    resolved: 0,
    closed: 0,
    hold: 0,
    totaltickets: 0,
  });

  const currentDate = new Date();
  const oneMonthAgo = new Date();
  oneMonthAgo.setMonth(currentDate.getMonth() - 1);
  const formatDateForInput = (date) => date.toISOString().split('T')[0];
  const [fromDate, setFromDate] = useState(formatDateForInput(oneMonthAgo));
  const [toDate, setToDate] = useState(formatDateForInput(currentDate));

  const UserId = sessionStorage.getItem("UserId");
  const assignBy = sessionStorage.getItem("UserName");
  const navigate = useNavigate();
  const globalProperty = useSelector(selectGlobalProperty);

  const getStatusChip = (status, theme) => {
    let color, icon, bgColor;
    switch (status) {
      case 'O':
        color = 'error';
        icon = <PriorityHighIcon fontSize='small' />; // Updated icon
        bgColor = theme.palette.error.main; // Unassigned
        break;
      case 'I':
        color = 'warning';
        icon = <AutorenewIcon fontSize='small' />;
        bgColor = theme.palette.warning.main; // In Progress
        break;
      case 'R':
        color = 'default';
        icon = <DoneAllIcon fontSize='small' />;
        bgColor = '#FF5722'; // Resolved
        break;
      case 'C':
        color = 'success';
        icon = <CancelIcon fontSize='small' />;
        bgColor = theme.palette.success.main; // Closed
        break;
      default:
        color = 'default';
        icon = null;
        bgColor = theme.palette.grey[200];
    }
    return (
      <Chip
        icon={icon}
        label={getStatusText(status)}
        color={color}
        size='small'
        sx={{
          fontWeight: 600,
          borderRadius: '12px',
          backgroundColor: bgColor,
          color: theme.palette.getContrastText(bgColor),
          '& .MuiChip-icon': {
            color: theme.palette.getContrastText(bgColor),
            fontSize: '0.80rem',
          },
          fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.80rem' },
          padding: { xs: '3px 8px', sm: '4px 10px', md: '2px 1px' },
          marginLeft: '1px',
          height: 'auto',
          whiteSpace: 'normal',
        }}
      />
    );
  };

  const fetchCardData = async () => {
    if (!globalProperty || !selectedCategory?.Id || !selectedWarden?.Id) return;

    try {
      const response = await axiosInstance.post(
        "TrnTicketing/GetFilterTicketDashBoard",
        {
          PageNumber: 1,
          PageSize: 10,
          PropId: globalProperty,
          Id: selectedCategory.Id,
          WardenId: selectedWarden.Id,
          ServiceProvId: 1,
          SearchText: "",
          SearchFrom: fromDate,
          SearchTo: toDate,
          Flag: "PropStatusCount",
        }
      );

      const {
        data: { Status, Data },
      } = response;

      if (Status === 0 && Array.isArray(Data)) {
        const counts = {
          open: 0,
          inprogress: 0,
          resolved: 0,
          closed: 0,
          hold: 0,
        };

        Data.forEach((item) => {
          switch (item.Status) {
            case "O":
              counts.open = item.TotalCount;
              break;
            case "I":
              counts.inprogress = item.TotalCount;
              break;
            case "H":
              counts.hold = item.TotalCount;
              break;
            case "R":
              counts.resolved = item.TotalCount;
              break;
            case "C":
              counts.closed = item.TotalCount;
              break;
            default:
              break;
          }
        });

        counts.open = counts.open + counts.inprogress + counts.resolved + counts.closed;
        setTicketCounts(counts);
      } else {
        setTicketCounts({
          open: 0,
          inprogress: 0,
          resolved: 0,
          closed: 0,
          hold: 0
        });
      }
    } catch (error) {
      console.error("Error fetching card data:", error);
      setTicketCounts({
        open: 0,
        inprogress: 0,
        resolved: 0,
        closed: 0,
        hold: 0,
      });
      toast.error("Error fetching ticket counts. Please try again.", {
        autoClose: 1000,
      });
    }
  };

  useEffect(() => {
    const fetchWardenName = async () => {
      if (!globalProperty) return;

      try {
        const response = await axiosInstance.post(
          `/TrnTicketing/getPropertyWisedrp`,
          {
            PropId: globalProperty,
            Flag: "RaisedBy",
          }
        );
        const {
          data: { Status, Data },
        } = response;
        if (Status === 0 && Array.isArray(Data) && Data.length > 0) {
          const allWarden = { Id: 1, Name: "All" };
          setWarden([allWarden, ...Data]);
          setSelectedWarden(allWarden);
        } else {
          setWarden([]);
          setSelectedWarden(null);
          toast.warn("No wardens available.", {
            autoClose: 1000,
          });
        }
      } catch (error) {
        setWarden([]);
        setSelectedWarden(null);
        toast.error("Error fetching wardenName. Please try again", {
          autoClose: 1000,
        });
      }
    };

    fetchWardenName();
  }, [globalProperty]);

  useEffect(() => {
    const fetchCategoryName = async () => {
      if (!globalProperty) return;

      try {
        const response = await axiosInstance.post(
          `TrnTicketing/getPropertyWisedrp`,
          {
            PropId: globalProperty,
            flag: "Cat",
          }
        );
        const {
          data: { Status, Data },
        } = response;

        if (Status === 0 && Array.isArray(Data) && Data.length > 0) {
          const allCategory = { Id: 1, Name: "All" };
          setCategories([allCategory, ...Data]);
          setSelectedCategory(allCategory);
        } else {
          setCategories([]);
          setSelectedCategory(null);
        }
      } catch (error) {
        setCategories([]);
        setSelectedCategory(null);
        toast.error("Error fetching categoryName. Please try again", {
          autoClose: 1000,
        });
      }
    };

    fetchCategoryName();
  }, [globalProperty]);

  useEffect(() => {
    if (globalProperty && selectedCategory?.Id && selectedWarden?.Id) {
      if (isInitialFetch) {
        setSelectedStatus("I");
        fetchTickets("I");
        setIsInitialFetch(false);
      }
      fetchCardData();
    }
  }, [globalProperty, selectedCategory, selectedWarden, fromDate, toDate]);

  const fetchTickets = async (overrideStatus = selectedStatus) => {
    try {
      if (!globalProperty || !selectedCategory?.Id) {
        toast.error("Property or category is not set.");
        return;
      }
      const response = await axiosInstance.post(
        "TrnTicketing/GetFilterTicketDashBoard",
        {
          PageNumber: 1,
          PageSize: 10,
          PropId: globalProperty,
          Id: selectedCategory.Id,
          WardenId: selectedWarden?.Id || 1,
          ServiceProvId: 1,
          SearchText: overrideStatus,
          SearchFrom: fromDate,
          SearchTo: toDate,
          Flag: "TicketDash",
        }
      );

      const {
        data: { Status, Data },
      } = response;

      if (Status === 0 && Array.isArray(Data)) {
        setRows(Data);
        if (Data.length > 0) {
          setSelectedTicket(Data[0]);
          setSelectedRowCategoryId(Data[0].TktCategoryId);
        } else {
          setSelectedTicket(null);
          setSelectedRowCategoryId(null);
        }
      } else {
        setRows([]);
        setSelectedTicket(null);
        setSelectedRowCategoryId(null);
      }
    } catch (error) {
      console.error("Error fetching tickets:", error);
      setRows([]);
      setSelectedTicket(null);
      setSelectedRowCategoryId(null);
      toast.error("Error fetching tickets. Please try again.", {
        autoClose: 1000,
      });
    }
  };

  const handleCategoryChange = (event, newValue) => {
    setSelectedCategory(newValue ?? null);
    setSelectedRows([]);
    setSelectedCategoryId(null);
  };

  const handleWardenChange = (event, newValue) => {
    setSelectedWarden(newValue ?? null);
    setSelectedRows([]);
    setSelectedCategoryId(null);
  };

  const handleStatusClick = (status) => {
    return (event) => {
      event.preventDefault();
      setSelectedStatus(status);
      setSelectedRows([]);
      setSelectedCategoryId(null);
      fetchTickets(status);
    };
  };

  const handleRowClick = (row) => {
    setSelectedTicket(row);
    setSelectedRowCategoryId(row.TktCategoryId);
    setDialogOpen(true);
  };

  const handleMultiDialogClose = () => {
    setMultiDialogOpen(false);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleDialogSubmit = async (ticketId, acceptance, remark) => {
    const updatedBy = UserId;

    try {
      if (acceptance !== null) {
        const acceptFlg = acceptance === "yes" ? "Y" : "N";
        const reason = acceptance === "no" ? remark : "";

        const acceptResponse = await axiosInstance.post(
          "TrnTicketing/AcceptTrnTicketByServProvider",
          {
            TicketId: ticketId,
            AcceptFlg: acceptFlg,
            Reason: reason,
            UpdatedBy: updatedBy,
          }
        );

        const {
          data: { Status, Message },
        } = acceptResponse;
        if (Status === 0 && Message) {
          toast.success(Message, { autoClose: 1000 });
        }
      }
      setSelectedTicket(null);
      setDialogOpen(false);
      fetchTickets();
      fetchCardData();
    } catch (error) {
      console.error(
        "Error during submission: " +
        (error.response?.data?.message || error.message)
      );
      toast.error("Error during submission. Please try again.");
    }
  };

  const handleGetData = () => {
    if (!globalProperty) {
      toast.error("Property is not set.", {
        autoClose: 1000,
      });
      return;
    }

    if (!selectedCategory || typeof selectedCategory !== 'object' || !selectedCategory.Id) {
      toast.warn("Please select a category before fetching tickets.", {
        position: "top-center",
        autoClose: 1500,
      });
      return;
    }
    setSelectedStatus('');
    fetchTickets("");
    fetchCardData();
  };

  const handleUpdate = async () => {
    // Placeholder if needed
  };

  const handleCancel = () => {
    setSelectedRows([]);
    setSelectedCategoryId(null);
  };

  const cardData = [
    { icon: <PriorityHighIcon />, label: "Un Assigned", value: ticketCounts.open, status: "O" },
    { icon: <AutorenewIcon />, label: "In Progress", value: ticketCounts.inprogress, status: "I" },
    { icon: <PauseIcon />, label: "Hold", value: ticketCounts.hold, status: "H" },
    { icon: <DoneAllIcon />, label: "Resolved", value: ticketCounts.resolved, status: "R" },
    { icon: <CancelIcon />, label: "Closed", value: ticketCounts.closed, status: "C" },
    { icon: <EqualizerIcon />, label: "Total Tickets", value: ticketCounts.totaltickets, status: "" },
  ];


  return (
    <Grid container spacing={0}>
      <ToastContainer />
      <Grid item xs={12} sx={{
        overflowY: { xs: 'auto', sm: 'auto', md: 'hidden' },
        overflowX: { xs: 'hidden', sm: 'hidden', md: 'hidden' },
        pt: { xs: 0, sx: 1, md: 0 },
        pb: { xs: 2, sx: 1, md: 1 },
      }}>
        <Grid
          container
          sx={{
            px: { xs: 2, sm: 2, md: 3 },
            py: { xs: 1, sm: 0, md: 0 },
            bgcolor: theme.palette.background.default,
            minHeight: '85vh',
          }}
        >
          <Grid container spacing={1} alignItems="center" sx={{ marginBlock: 0.5 }}>
            <Grid container spacing={1} alignItems="center" sx={{ marginBlock: 0.5 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: { xs: "center", sm: "flex-start" },
                    cursor: "pointer",
                    width: "100%",
                    gap: { xs: 1, sm: 2 },
                    marginBottom: { xs: '5px', sm: '1px', md: '0px' }
                  }}
                >
                  <Tooltip title="Go Back" arrow onClick={() => navigate("/service&ticket/complaint")}>
                    <Avatar
                      sx={{
                        bgcolor: theme.palette.primary.main,
                        width: { xs: 30, sm: 40, md: 40 },
                        height: { xs: 30, sm: 40, md: 40 },
                        boxShadow: theme.shadows[4],
                        cursor: "pointer",
                      }}
                    >
                      <KeyboardBackspaceIcon
                        sx={{
                          fontSize: { xs: 20, sm: 24, md: 28 },
                          color: "#fff",
                        }}
                      />
                    </Avatar>
                  </Tooltip>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      letterSpacing: "-0.5px",
                      background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    Task Acceptance
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6} md={9}>
                <Grid container spacing={1} alignItems="center" justifyContent={{ xs: "flex-start", sm: "flex-start", md: "flex-end" }}>
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      label="From Date"
                      type="date"
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      size="small"
                      fullWidth
                      sx={{
                        "& .MuiInputBase-root": {
                          fontSize: { xs: "0.97rem", sm: "0.875rem" },
                          height: { xs: "40px", sm: "36px" },
                        },
                        "& .MuiInputLabel-root": {
                          fontSize: { xs: "0.97rem", sm: "0.875rem" },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      label="To Date"
                      type="date"
                      value={toDate}
                      onChange={(e) => setToDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      size="small"
                      fullWidth
                      sx={{
                        "& .MuiInputBase-root": {
                          fontSize: { xs: "0.97rem", sm: "0.875rem" },
                          height: { xs: "40px", sm: "36px" },
                        },
                        "& .MuiInputLabel-root": {
                          fontSize: { xs: "0.97rem", sm: "0.875rem" },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2.5}>
                    <Autocomplete
                      key={warden.length}
                      options={warden}
                      getOptionLabel={(option) => option.Name || ""}
                      value={selectedWarden}
                      onChange={handleWardenChange}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="RaisedBy"
                          variant="outlined"
                          size="small"
                          fullWidth
                          placeholder={warden.length === 0 ? "No wardens available" : "Select a warden"}
                          sx={{
                            "& .MuiInputBase-root": {
                              fontSize: { xs: "0.99rem", sm: "0.875rem" },
                              height: { xs: "40px", sm: "36px" },
                            },
                            "& .MuiInputLabel-root": {
                              fontSize: { xs: "0.99rem", sm: "0.875rem" },
                            },
                          }}
                        />
                      )}
                      isOptionEqualToValue={(option, value) => option.Id === value?.Id}
                      noOptionsText="No wardens available"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={5}>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      <Autocomplete
                        key={categories.length}
                        options={categories}
                        getOptionLabel={(option) => option.Name || ""}
                        value={selectedCategory}
                        onChange={handleCategoryChange}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Category"
                            variant="outlined"
                            size="small"
                            fullWidth
                            placeholder={categories.length === 0 ? "No categories available" : "Select a category"}
                            sx={{
                              "& .MuiInputBase-root": {
                                fontSize: { xs: "0.99rem", sm: "0.875rem" },
                                height: { xs: "40px", sm: "36px" },
                              },
                              "& .MuiInputLabel-root": {
                                fontSize: { xs: "0.99rem", sm: "0.875rem" },
                              },
                            }}
                          />
                        )}
                        isOptionEqualToValue={(option, value) => option.Id === value?.Id}
                        noOptionsText="No categories available"
                        sx={{ flex: 1 }}
                      />
                      <Box sx={{ display: { xs: "none", sm: "none", md: "flex" }, gap: 1 }}>
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          onClick={handleGetData}
                          sx={{
                            borderRadius: "4px",
                            textTransform: "none",
                            fontWeight: "bold",
                            px: { xs: 1, sm: 2 },
                            fontSize: { xs: "0.87rem", sm: "0.75rem", md: "0.875rem" },
                            height: { xs: "40px", sm: "36px" },
                            minWidth: { xs: "80px", sm: "80px", md: "80px" },
                          }}
                        >
                          Get Data
                        </Button>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid container item xs={12} sm={12} sx={{ display: { xs: "flex", md: "none" }, justifyContent: "center" }} >
                    <Grid item xs={12} sm={3} sx={{ display: "flex", justifyContent: "center" }}>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={handleGetData}
                        fullWidth
                        sx={{
                          borderRadius: "4px",
                          textTransform: "none",
                          fontWeight: "bold",
                          px: { xs: 1, sm: 2 },
                          fontSize: { xs: "1rem", sm: "0.75rem", md: "0.875rem" },
                          height: { xs: "40px", sm: "36px" },
                          maxWidth: { xs: "200px", sm: "200px" },
                          margin: "0 auto",
                        }}
                      >
                        Get Data
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            <Grid container spacing={1} sx={{ mt: { xs: 0, sm: 2, md: 0 } }}>
              {cardData.map((card, index) => (
                <Grid item xs={12} sm={6} md={2.4} key={index}>
                  <StyledCard
                    onClick={handleStatusClick(card.status)}
                    sx={{
                      cursor: 'pointer',
                      border: selectedStatus === card.status ? `2px solid ${theme.palette.primary.main}` : `1px solid ${theme.palette.divider}`,
                      width: '100%',
                    }}
                  >
                    <Grid container sx={{ display: 'flex', alignItems: 'center' }}>
                      <StyledAvatar status={card.status}>{card.icon}</StyledAvatar>
                      <Grid item sx={{ ml: 2.5 }}>
                        <Typography
                          variant='body2'
                          color='text.secondary'
                          sx={{
                            fontWeight: 500,
                            fontSize: { xs: '1rem', sm: '0.875rem' },
                          }}
                        >
                          {card.label}
                        </Typography>
                        <Typography
                          variant='subtitle1'
                          sx={{
                            fontWeight: 700,
                            fontSize: { xs: '1rem', sm: '1rem' },
                          }}
                        >
                          {card.value || '0'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </StyledCard>
                </Grid>
              ))}
            </Grid>

            <TaskAcceptanceTable
              rows={rows}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              selectedTicket={selectedTicket}
              handleRowClick={handleRowClick}
              handleUpdate={handleUpdate}
              getStatusChip={(status) => getStatusChip(status, theme)}
              selectedRows={selectedRows}
              setSelectedRows={setSelectedRows}
              handleCancel={handleCancel}
              selectedCategoryId={selectedCategoryId}
              setSelectedCategoryId={setSelectedCategoryId}
            />
            <TaskAcceptDialog
              open={dialogOpen}
              onClose={handleDialogClose}
              ticket={selectedTicket}
              onSubmit={handleDialogSubmit}
              getStatusChip={(status) => getStatusChip(status, theme)}
            />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
}